import { Box, Flex } from '@chakra-ui/layout';
import { Button, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import React, { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { InputContainer, TextInput, DateInput, SelectInput } from './Inputs';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { ConfirmDeleteModal } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/facility-mechanics/ConfirmDeleteModal';

type FormComponentProps = {
  deleteProperty: () => void;
};

const AmoritizationPeriodStart = ({ deleteProperty }: FormComponentProps) => (
  <InputContainer
    label="Amortization Period Start"
    deleteProperty={deleteProperty}
  >
    <DateInput
      name="amoritizationPeriodStart"
      label="Date"
      placeholder="Enter Date"
    />
  </InputContainer>
);

const CallProtectionExpiration = ({ deleteProperty }: FormComponentProps) => (
  <InputContainer
    label="Call Protection Expiration"
    deleteProperty={deleteProperty}
  >
    <DateInput
      name="callProtectionExpiration"
      label="Date"
      placeholder="Enter Date"
    />
  </InputContainer>
);

const CallProtectionValue = ({ deleteProperty }: FormComponentProps) => (
  <InputContainer label="Call Protection Value" deleteProperty={deleteProperty}>
    <TextInput name="callProtectionValue" label="Value" />
  </InputContainer>
);

const ClosingDate = ({ deleteProperty }: FormComponentProps) => (
  <InputContainer label="Closing Date" deleteProperty={deleteProperty}>
    <DateInput name="closingDate" label="Date" placeholder="Enter Date" />
  </InputContainer>
);

const GoverningLaw = ({ deleteProperty }: FormComponentProps) => (
  <InputContainer label="Governing Law" deleteProperty={deleteProperty}>
    <TextInput name="governingLaw" label="Location" placeholder="City, State" />
  </InputContainer>
);

const MaturityDate = ({ deleteProperty }: FormComponentProps) => (
  <InputContainer label="Maturity Date" deleteProperty={deleteProperty}>
    <DateInput name="maturityDate" label="Date" placeholder="Enter Date" />
    <Box py="0.5rem" />
    <TextInput name="maturityDateDescription" label="Description" />
  </InputContainer>
);

const MinimumUtilization = ({ deleteProperty }: FormComponentProps) => (
  <InputContainer label="Minimum Utilization" deleteProperty={deleteProperty}>
    <TextInput name="minimumUtilization" label="Value" />
  </InputContainer>
);

const Structure = ({ deleteProperty }: FormComponentProps) => (
  <InputContainer label="Structure" deleteProperty={deleteProperty}>
    <SelectInput
      name="structure"
      label="Type"
      placeholder="Select type"
      options={[
        { label: 'Fixed Proportion', value: 'fixedProportion' },
        { label: 'Classed', value: 'classed' },
      ]}
    />
  </InputContainer>
);

const YieldMaintenance = ({ deleteProperty }: FormComponentProps) => (
  <InputContainer label="Yield Maintenance" deleteProperty={deleteProperty}>
    <DateInput name="yieldMaintenance" label="Date" placeholder="Enter Date" />
  </InputContainer>
);

type PropertyItem = {
  name: string;
  label: string;
  component: React.FC<FormComponentProps>;
};

const propertyItems: PropertyItem[] = [
  {
    name: 'amoritizationPeriodStart',
    label: 'Amortization Period Start',
    component: AmoritizationPeriodStart,
  },
  {
    name: 'callProtectionExpiration',
    label: 'Call Protection Expiration',
    component: CallProtectionExpiration,
  },
  {
    name: 'callProtectionValue',
    label: 'Call Protection Value',
    component: CallProtectionValue,
  },
  { name: 'closingDate', label: 'Closing Date', component: ClosingDate },
  { name: 'structure', label: 'Structure', component: Structure },
  { name: 'governingLaw', label: 'Governing Law', component: GoverningLaw },
  {
    name: 'maturityDate',
    label: 'Maturity Date',
    component: MaturityDate,
  },
  {
    name: 'minimumUtilization',
    label: 'Minimum Utilization',
    component: MinimumUtilization,
  },
  {
    name: 'yieldMaintenance',
    label: 'Yield Maintenance',
    component: YieldMaintenance,
  },
];

type FormFields = {
  amoritizationPeriodStart?: Date;
  callProtectionExpiration?: Date;
  callProtectionValue?: string;
  closingDate?: Date;
  governingLaw?: string;
  maturityDate?: Date;
  maturityDateDescription?: string;
  minimumUtilization?: string;
  structure?: 'fixedProportion' | 'classed';
  yieldMaintenance?: Date;
};

export const FacilityMechanics = () => {
  const methods = useForm<FormFields>({ shouldUnregister: true });
  const [properties, setProperties] = useState<PropertyItem[]>([]);
  const [propertyToDelete, setPropertyToDelete] = useState<PropertyItem | null>(
    null,
  );

  const onSubmit = () => undefined;

  const appendProperty = (property: PropertyItem) =>
    setProperties((prevProperties) => [...prevProperties, property]);

  const deleteProperty = (property: PropertyItem | null) => {
    if (property !== null) {
      setProperties((prevProperties) =>
        prevProperties.filter(
          (prevProperty) => prevProperty.name !== property.name,
        ),
      );
    }
  };

  const propertySet = new Set(properties.map((property) => property.name));

  return (
    <Flex direction="column">
      <ConfirmDeleteModal
        propertyToDelete={propertyToDelete}
        onClose={() => setPropertyToDelete(null)}
        onDelete={() => {
          deleteProperty(propertyToDelete);
          setPropertyToDelete(null);
        }}
      />
      <Flex justify="space-between" mb="1rem">
        <Typography variant="page-title">Facility Mechanics</Typography>
        <Menu>
          <MenuButton
            variant="outline"
            as={Button}
            leftIcon={<Icon icon="plus" />}
            rightIcon={<Icon icon="chevron-down" />}
          >
            Add Property
          </MenuButton>
          <MenuList>
            {propertyItems.map((property) => (
              <MenuItem
                key={property.name}
                isDisabled={propertySet.has(property.name)}
                onClick={() => appendProperty(property)}
              >
                {property.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Flex>

      {properties.length === 0 && (
        <Typography variant="base-muted">
          No properties have been added yet. Use the &quot;Add Property&quot;
          button above to select and configure the desired fields.
        </Typography>
      )}

      {properties.length > 0 && (
        <>
          <Typography variant="section-subtitle">Properties</Typography>
          <Box>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                {properties.map((property) => {
                  const Component = property.component;
                  return (
                    <Component
                      key={property.name}
                      deleteProperty={() => setPropertyToDelete(property)}
                    />
                  );
                })}
              </form>
            </FormProvider>
          </Box>
          <Flex justify="end" gap="1.5rem" mt="2.5rem">
            <Button
              variant="outline"
              colorScheme="v2-aurora"
              onClick={() => {
                methods.getValues();
              }}
            >
              Save Draft
            </Button>
            <Button variant="primary" colorScheme="v2-aurora">
              Submit
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  );
};
